import type {
  AbilityContext,
  AppAbility,
  EndUserRole,
  SystemRole,
} from "@adva/auth";
import { defineAbilityFor } from "@adva/auth";

// ADV-881: The capability matrix at /settings/permissions renders the same
// PermissionsGrid component once per role. To do that, we build a synthetic
// AbilityContext for each role and feed it through defineAbilityFor — exactly
// the same function the live AbilityProvider uses. Output drifts automatically
// when the ability definitions change in @adva/auth.

export interface RolePreset {
  /** Stable key used for tab values and React keys. */
  key: string;
  /** Short label shown on the tab trigger. */
  label: string;
  /** One-line explanation shown under the tab heading. */
  description: string;
  systemRole: SystemRole;
  endUserRole: EndUserRole | null;
}

export const ROLE_PRESETS: readonly RolePreset[] = [
  {
    key: "super_admin",
    label: "Super Admin",
    description:
      "Adva platform staff. Cross-account access to every business, plus reset, backup, and restore.",
    systemRole: "super_admin",
    endUserRole: null,
  },
  {
    key: "admin",
    label: "Admin",
    description:
      "Manages the account and every business under it. Cannot transfer ownership or access other accounts.",
    systemRole: "admin",
    endUserRole: null,
  },
  {
    key: "billing",
    label: "Billing",
    description:
      "Limited to invoices, payment methods, and subscriptions. Read-only on operational data.",
    systemRole: "billing",
    endUserRole: null,
  },
  {
    key: "user_manager",
    label: "User · Manager",
    description:
      "Day-to-day operator with full create, update, and delete on opportunities, jobs, customers, and proposals within the active business.",
    systemRole: "user",
    endUserRole: "manager",
  },
  {
    key: "user_salesperson",
    label: "User · Salesperson",
    description:
      "Sees opportunities, customers, and proposals they own (and unassigned ones). Cannot read records assigned to other salespeople.",
    systemRole: "user",
    endUserRole: "salesperson",
  },
  {
    key: "user_crew_chief",
    label: "User · Crew Chief",
    description:
      "Sees jobs they are assigned to lead, plus customers in the business. Cannot read opportunities or proposals.",
    systemRole: "user",
    endUserRole: "crew_chief",
  },
];

export interface PresetAbilityScope {
  accountId: string;
  businessId: string | null;
  /**
   * The user id used to materialize per-user conditions (e.g. salesperson
   * `salesperson_user_id` and crew chief `crew_chief_user_id`). For the
   * capability matrix this is the viewer's user id — the matrix shows
   * "what a salesperson whose ID equals you would be able to do."
   */
  userId: string | null;
}

/**
 * Build an AppAbility for a role preset. Pure function — same inputs always
 * produce the same ruleset, which is what makes the matrix deterministic.
 */
export function buildAbilityForPreset(
  preset: RolePreset,
  scope: PresetAbilityScope,
): AppAbility {
  const ctx: AbilityContext = {
    systemRole: preset.systemRole,
    endUserRole: preset.endUserRole,
    accountId: scope.accountId,
    businessId: scope.businessId,
    userId: scope.userId,
  };
  return defineAbilityFor(ctx);
}
